// Modal form for /accounts → "Create account". Mirrors the
// orixen-hub create-account CLI. account_kind is constrained to the
// three backend literals via SelectField; slug carries the same
// client-side regex as every other create form.

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { accounts } from "../../api/endpoints/accounts";
import { qk } from "../../api/queryKeys";
import { FormShell } from "../../components/forms/FormShell";
import { SelectField } from "../../components/forms/SelectField";
import { TextField } from "../../components/forms/TextField";
import { slugify } from "../../lib/slug";
import type { AccountCreate, AccountKind } from "../../api/types/account";

interface AccountCreateFormProps {
  onDone: () => void;
  onPendingChange?: (pending: boolean) => void;
}

const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;
const KIND_OPTIONS: ReadonlyArray<{ value: AccountKind; label: string }> = [
  { value: "business", label: "Business (e.g. Khired)" },
  { value: "family", label: "Family (e.g. Khawaja household)" },
  { value: "internal", label: "Internal" },
];

interface FormState {
  slug: string;
  display_name: string;
  account_kind: AccountKind;
}

const EMPTY: FormState = {
  slug: "",
  display_name: "",
  account_kind: "business",
};

export function AccountCreateForm({
  onDone,
  onPendingChange,
}: AccountCreateFormProps) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<FormState>(EMPTY);
  // While false, the slug field auto-syncs from slugify(display_name).
  // First hand-edit of the slug flips this; intent always wins from
  // there. Latch resets on successful submit so the next account
  // starts fresh.
  const slugTouched = useRef(false);

  const slugError = useMemo(() => {
    if (!values.slug) return null;
    return SLUG_RE.test(values.slug)
      ? null
      : "Slug must match a-z, 0-9, hyphen; 1–63 chars; cannot start with hyphen.";
  }, [values.slug]);

  const mutation = useMutation({
    mutationFn: () => {
      const body: AccountCreate = {
        slug: values.slug,
        display_name: values.display_name,
        account_kind: values.account_kind,
      };
      return accounts.create(body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.accounts() });
      setValues(EMPTY);
      slugTouched.current = false; // next account starts fresh with auto-suggest
      onDone();
    },
  });

  useEffect(() => {
    onPendingChange?.(mutation.isPending);
  }, [mutation.isPending, onPendingChange]);

  const isValid =
    values.slug !== "" &&
    SLUG_RE.test(values.slug) &&
    values.display_name !== "";

  return (
    <FormShell
      submitLabel="Create account"
      onSubmit={(e) => {
        e.preventDefault();
        if (!isValid) return;
        mutation.mutate();
      }}
      onCancel={onDone}
      pending={mutation.isPending}
      error={mutation.error}
      errorTitle="Create account failed"
    >
      <TextField
        label="Display name"
        value={values.display_name}
        onChange={(display_name) =>
          setValues((v) => ({
            ...v,
            display_name,
            slug: slugTouched.current ? v.slug : slugify(display_name),
          }))
        }
        required
        placeholder="Khired"
        helper="The human-readable name. Slug below is auto-derived; edit it if you want something shorter."
      />
      <TextField
        label="Slug"
        value={values.slug}
        onChange={(slug) => {
          slugTouched.current = true;
          setValues((v) => ({ ...v, slug }));
        }}
        required
        placeholder="khired"
        helper="URL-safe identifier. Auto-derived from display name until you edit it. There is no rename — typos stick."
        error={slugError}
      />
      <SelectField
        label="Kind"
        value={values.account_kind}
        onChange={(account_kind) =>
          setValues((v) => ({ ...v, account_kind: account_kind as AccountKind }))
        }
        required
        options={KIND_OPTIONS}
        helper="business = company / agency · family = household · internal = Orixen-owned"
      />
    </FormShell>
  );
}
